import type {
  InboxViewRule,
  InboxViewRuleDraft,
  InboxViewScreenedOut,
  InboxViewScreenedOutKind,
} from "@/types/inboxView/inboxView";

export type RuleSection = "time" | "text" | "score";

export const RULE_SECTIONS: { id: RuleSection; label: string }[] = [
  { id: "text", label: "What was said" },
  { id: "time", label: "Time" },
  { id: "score", label: "Score" },
];

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const ruleSection = (rule: InboxViewRule | InboxViewRuleDraft): RuleSection =>
  rule.kind === "time" ? "time" : rule.kind === "score" ? "score" : "text";

const atLeast = (op: "gte" | "lte") => (op === "gte" ? "at least" : "at most");

const duration = (seconds: number) =>
  seconds % 60 === 0 && seconds >= 60 ? `${seconds / 60} min` : `${seconds} sec`;

/** One-line, customer-readable wording of a rule. */
export function describeRule(rule: InboxViewRule | InboxViewRuleDraft): string {
  switch (rule.kind) {
    case "ai":
      return rule.negate ? `Not: ${rule.text}` : rule.text;
    case "field": {
      const format = rule.format === "phone" ? " (a phone number)" : rule.format === "email" ? " (an email address)" : "";
      switch (rule.op) {
        case "filled":
          return `${rule.column} is given${format}`;
        case "empty":
          return `${rule.column} is empty`;
        case "contains":
          return `${rule.column} contains "${rule.value}"`;
        case "not_contains":
          return `${rule.column} does not contain "${rule.value}"`;
        case "equals_any":
          return `${rule.column} is ${(Array.isArray(rule.value) ? rule.value : [rule.value]).join(" or ")}`;
        default:
          return `${rule.column} is ${atLeast(rule.op)} ${rule.value}`;
      }
    }
    case "time":
      switch (rule.metric) {
        case "occurred": {
          const days = rule.weekdays?.length ? rule.weekdays.map((d) => WEEKDAYS[d]).join(", ") : "any day";
          const hours = rule.hours ? `${rule.hours.from}–${rule.hours.to}` : "any time";
          return rule.outside ? `Happened outside ${days}, ${hours}` : `Happened on ${days}, ${hours}`;
        }
        case "duration":
          return `Lasted ${atLeast(rule.op)} ${duration(rule.seconds)}`;
        case "caller_turns":
          return `Caller spoke ${atLeast(rule.op)} ${rule.value} times`;
        case "deadline":
          return rule.within_days ? `Caller needs it within ${rule.within_days} days` : "Caller gave a deadline";
        case "age":
          return `Still waiting after ${rule.hours} hours`;
      }
      break;
    case "score":
      return `${rule.score === "urgency" ? "Urgency" : "Buying intent"} ${atLeast(rule.op)} ${rule.value}`;
  }
  return "";
}

const SCREENED_OUT_LABELS: Record<InboxViewScreenedOutKind, string> = {
  must_rule: "missing a must-have",
  low_match: "below the minimum match",
  time: "outside the time conditions",
  not_real: "test, silent or hang-up",
  empty: "nothing relevant",
};

/** "12 missing a must-have · 3 test, silent or hang-up" */
export function describeScreenedOut(screenedOut: InboxViewScreenedOut): string {
  return (Object.keys(SCREENED_OUT_LABELS) as InboxViewScreenedOutKind[])
    .filter((kind) => (screenedOut.byKind[kind] ?? 0) > 0)
    .map((kind) => `${screenedOut.byKind[kind]} ${SCREENED_OUT_LABELS[kind]}`)
    .join(" · ");
}
